import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import ShareButtons from '../../../components/ui/ShareButtons';

/**
 * Extract domain from a website URL for logo fetching.
 */
const getDomain = (url) => {
  if (!url) return null;
  try {
    return new URL(url).hostname.replace('www.', '');
  } catch {
    return null;
  }
};

/**
 * CompanyLogo — Smart logo component with zero-API-call priority.
 *
 * Priority chain:
 *   1. Supabase Storage URL (company.logoUrl) — PRE-FETCHED, instant, no API call
 *   2. Logo.dev          — high quality, needs VITE_LOGO_DEV_TOKEN in .env
 *   3. Unavatar.io       — free aggregator (Google, DuckDuckGo, etc.)
 *   4. Google Favicons   — nearly universal
 *   5. Colored initial   — guaranteed fallback
 *
 * Logos are pre-fetched by scripts/fetch_and_store_logos.js and stored in Supabase.
 * External API calls only happen for companies NOT yet in the database.
 */
const LOGO_DEV_TOKEN = import.meta.env.VITE_LOGO_DEV_TOKEN || '';

const getExternalLogoSources = (domain, size) => {
  const retina = Math.max(size * 2, 64);
  const sources = [];
  if (LOGO_DEV_TOKEN) {
    sources.push(`https://img.logo.dev/${domain}?token=${LOGO_DEV_TOKEN}&size=${retina}&format=webp&fallback=monogram`);
  }
  sources.push(`https://unavatar.io/${domain}?fallback=false`);
  sources.push(`https://www.google.com/s2/favicons?domain=${domain}&sz=${retina}`);
  return sources;
};

const CompanyLogo = ({ company, size = 18 }) => {
  const [sourceIndex, setSourceIndex] = useState(0);
  const [allFailed, setAllFailed] = useState(false);

  // Priority 1: Pre-stored logo from Supabase (no API call!)
  const storedLogo = company?.logoUrl;
  const domain = getDomain(company?.website);

  // Build external fallback sources (only used if no stored logo or it fails)
  const externalSources = domain ? getExternalLogoSources(domain, size) : [];

  // Combine: stored logo first, then external sources
  const allSources = [
    ...(storedLogo ? [storedLogo] : []),
    ...externalSources,
  ];

  const handleError = () => {
    const nextIndex = sourceIndex + 1;
    if (nextIndex < allSources.length) {
      setSourceIndex(nextIndex);
    } else {
      setAllFailed(true);
    }
  };

  if (!allFailed && allSources.length > 0) {
    return (
      <img
        src={allSources[sourceIndex]}
        alt={company?.name}
        title={company?.name}
        className="rounded-full border border-gray-200 bg-white object-contain"
        style={{ width: size, height: size }}
        onError={handleError}
        loading="lazy"
      />
    );
  }

  // Final fallback: colored initial circle
  const initial = (company?.name || '?')[0].toUpperCase();
  const colors = ['bg-blue-500', 'bg-indigo-500', 'bg-emerald-500', 'bg-amber-500', 'bg-pink-500', 'bg-cyan-500'];
  const color = colors[(company?.name?.length || 0) % colors.length];

  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-bold ${color}`}
      style={{ width: size, height: size, fontSize: size * 0.5 }}
      title={company?.name}
    >
      {initial}
    </div>
  );
};

/**
 * Organization initial badge (for charities, think tanks, PACs without logos).
 */
const OrgBadge = ({ name, color = 'bg-pink-500', size = 18 }) => {
  const initial = (name || '?')[0].toUpperCase();
  return (
    <div
      className={`rounded-full flex items-center justify-center text-white font-bold ${color}`}
      style={{ width: size, height: size, fontSize: size * 0.5 }}
      title={name}
    >
      {initial}
    </div>
  );
};

/**
 * Format large currency amounts.
 */
const formatAmount = (amount) => {
  if (!amount) return null;
  if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return `$${amount.toLocaleString()}`;
};

/**
 * Expandable stat row — clickable with inline previews.
 */
const StatRow = ({ icon, iconColor, label, count, items, renderItem, previewItems, navigate }) => {
  const [expanded, setExpanded] = useState(false);
  const hasItems = items && items.length > 0;

  return (
    <div>
      <div
        className={`flex justify-between items-center py-1.5 px-1 rounded-md transition-colors ${
          hasItems ? 'cursor-pointer hover:bg-gray-50 active:bg-gray-100' : ''
        }`}
        onClick={(e) => {
          if (hasItems) {
            e.stopPropagation();
            setExpanded(!expanded);
          }
        }}
      >
        <span className="text-[11px] text-gray-500 flex items-center gap-1 flex-shrink-0">
          <Icon name={icon} size={11} className={iconColor} />
          {label}
        </span>
        <div className="flex items-center gap-1.5">
          {/* Inline logo previews */}
          {previewItems && previewItems.length > 0 && (
            <div className="flex -space-x-1.5">
              {previewItems.slice(0, 4).map((item, idx) => (
                <div key={idx} className="relative" style={{ zIndex: 4 - idx }}>
                  {item}
                </div>
              ))}
              {previewItems.length > 4 && (
                <div
                  className="rounded-full bg-gray-200 flex items-center justify-center text-[8px] font-bold text-gray-600 border border-white"
                  style={{ width: 18, height: 18 }}
                >
                  +{previewItems.length - 4}
                </div>
              )}
            </div>
          )}
          <span className="text-xs font-bold text-gray-800 min-w-[16px] text-right">
            {count}
          </span>
          {hasItems && (
            <Icon
              name={expanded ? 'ChevronUp' : 'ChevronDown'}
              size={12}
              className="text-gray-400 flex-shrink-0"
            />
          )}
        </div>
      </div>

      {/* Expanded detail section */}
      {expanded && hasItems && (
        <div
          className="ml-4 mr-1 mb-1.5 mt-0.5 space-y-1 border-l-2 border-gray-200 pl-2 animate-fade-in"
          onClick={(e) => e.stopPropagation()}
        >
          {items.slice(0, 6).map((item, idx) => (
            <div key={idx}>
              {renderItem(item, navigate)}
            </div>
          ))}
          {items.length > 6 && (
            <div className="text-[10px] text-gray-400 italic pl-1">
              +{items.length - 6} more...
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * ProminentFigureCard — rich profile card with clickable, expandable stat rows
 * showing inline company logos and navigable detail items.
 */
const ProminentFigureCard = ({ individual, onClick }) => {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);

  const formatNetWorth = (amount) => {
    if (!amount) return 'N/A';
    if (amount >= 1_000_000_000_000) return `$${(amount / 1_000_000_000_000).toFixed(1)}T`;
    if (amount >= 1_000_000_000) return `$${(amount / 1_000_000_000).toFixed(1)}B`;
    if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
    if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
    return `$${amount.toLocaleString()}`;
  };

  const getPoliticalLabel = (leaning) => {
    switch (leaning) {
      case 'conservative': return 'Right';
      case 'liberal': return 'Left';
      default: return 'Center';
    }
  };

  const getPoliticalBgColor = (leaning) => {
    switch (leaning) {
      case 'conservative': return 'bg-red-100 text-red-800 border-red-200';
      case 'liberal': return 'bg-blue-100 text-blue-800 border-blue-200';
      default: return 'bg-purple-100 text-purple-800 border-purple-200';
    }
  };

  const getPoliticalDotColor = (leaning) => {
    switch (leaning) {
      case 'conservative': return 'bg-red-500';
      case 'liberal': return 'bg-blue-500';
      default: return 'bg-purple-500';
    }
  };

  const getSpectrumPosition = (score) => {
    return Math.max(2, Math.min(98, ((score + 100) / 200) * 100));
  };

  const handleClick = () => {
    if (onClick) {
      onClick(individual);
    } else if (individual?.id) {
      navigate(`/individual-profile?id=${individual.id}`);
    }
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').substring(0, 2).toUpperCase();
  };

  const hasImage = individual?.image && individual?.hasRealImage && !imageError;

  // ── Build data for each stat row ──

  // Companies Owned/Founded (from executives data)
  const ownedCompanies = individual?.executiveCompanies || [];
  const ownedLogoPreviews = ownedCompanies.map((c, i) => (
    <CompanyLogo key={i} company={c} size={18} />
  ));

  // Companies Invested In
  const investedCompanies = individual?.investmentCompanies || [];
  const investedLogoPreviews = investedCompanies.map((c, i) => (
    <CompanyLogo key={i} company={c} size={18} />
  ));

  // Charities
  const charities = individual?.charityDetails || [];
  const charityPreviews = charities.map((c, i) => (
    <OrgBadge key={i} name={c.name} color="bg-pink-500" size={18} />
  ));

  // PACs
  const pacs = individual?.pacDetails || [];
  const pacPreviews = pacs.map((p, i) => (
    <OrgBadge key={i} name={p.name} color="bg-amber-500" size={18} />
  ));

  // Think Tanks
  const thinkTanks = individual?.thinkTankDetails || [];
  const thinkTankPreviews = thinkTanks.map((t, i) => (
    <OrgBadge key={i} name={t.name} color="bg-teal-500" size={18} />
  ));

  // ── Render functions for expanded items ──

  const renderCompanyItem = (company, nav) => (
    <div
      className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-blue-50 cursor-pointer transition-colors group/item"
      onClick={() => nav(`/company-profile?id=${company.id}`)}
    >
      <CompanyLogo company={company} size={22} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-gray-800 truncate">{company.name}</div>
        <div className="text-[9px] text-gray-400 truncate">
          {company.position || company.investmentType || company.industry || ''}
          {company.equityPercentage > 0 && ` · ${company.equityPercentage}% equity`}
          {company.investmentAmount > 0 && ` · ${formatAmount(company.investmentAmount)}`}
        </div>
      </div>
      <Icon name="ArrowRight" size={10} className="text-gray-300 group-hover/item:text-blue-500 flex-shrink-0" />
    </div>
  );

  const renderCharityItem = (charity) => (
    <div className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-pink-50 transition-colors">
      <OrgBadge name={charity.name} color="bg-pink-500" size={22} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-gray-800 truncate">{charity.name}</div>
        <div className="text-[9px] text-gray-400 truncate">
          {charity.relationship || charity.type}
          {charity.amountDonated > 0 && ` · ${formatAmount(charity.amountDonated)}`}
        </div>
      </div>
    </div>
  );

  const renderPacItem = (pac) => {
    const affColor = (pac.affiliation || '').toLowerCase();
    const dotClass = affColor.includes('republican') || affColor.includes('conservative')
      ? 'bg-red-400'
      : affColor.includes('democrat') || affColor.includes('liberal')
      ? 'bg-blue-400'
      : 'bg-purple-400';

    return (
      <div className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-amber-50 transition-colors">
        <OrgBadge name={pac.name} color="bg-amber-500" size={22} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold text-gray-800 truncate flex items-center gap-1">
            {pac.name}
            <span className={`w-1.5 h-1.5 rounded-full ${dotClass} flex-shrink-0`}></span>
          </div>
          <div className="text-[9px] text-gray-400 truncate">
            {pac.relationship || 'affiliated'}
            {pac.amount > 0 && ` · ${formatAmount(pac.amount)}`}
          </div>
        </div>
      </div>
    );
  };

  const renderThinkTankItem = (tt) => (
    <div className="flex items-center gap-2 py-1 px-1.5 rounded hover:bg-teal-50 transition-colors">
      <OrgBadge name={tt.name} color="bg-teal-500" size={22} />
      <div className="flex-1 min-w-0">
        <div className="text-[11px] font-semibold text-gray-800 truncate">{tt.name}</div>
        <div className="text-[9px] text-gray-400 truncate">
          {tt.relationship || 'donor'}
          {tt.amountDonated > 0 && ` · ${formatAmount(tt.amountDonated)}`}
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="group relative bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 transform hover:scale-[1.015] border border-gray-200 hover:border-gray-300 cursor-pointer flex flex-col"
      onClick={handleClick}
    >
      {/* ── TOP SECTION: Photo + Name + Political Badge ── */}
      <div className="p-4 pb-3 flex items-start gap-3.5">
        {/* Portrait Image */}
        <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0 bg-gray-100 border border-gray-200">
          {hasImage ? (
            <img
              src={individual.image}
              alt={individual.imageAlt || `Photo of ${individual.name}`}
              className="w-full h-full object-cover"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : individual?.image && !imageError ? (
            <img
              src={individual.image}
              alt={individual.imageAlt || `Photo of ${individual.name}`}
              className="w-full h-full object-cover opacity-70"
              onError={() => setImageError(true)}
              loading="lazy"
            />
          ) : (
            <div className={`w-full h-full flex items-center justify-center text-white font-bold text-lg ${getPoliticalDotColor(individual?.politicalLeaning)}`}>
              {getInitials(individual?.name)}
            </div>
          )}
        </div>

        {/* Name, Title, Badge */}
        <div className="flex-1 min-w-0">
          <h3 className="font-bold text-gray-900 text-sm leading-tight truncate">
            {individual?.name}
          </h3>
          <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-snug">
            {individual?.title?.substring(0, 80)}
          </p>
          <div className="mt-1.5 flex items-center gap-1.5">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${getPoliticalBgColor(individual?.politicalLeaning)}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${getPoliticalDotColor(individual?.politicalLeaning)}`}></span>
              {getPoliticalLabel(individual?.politicalLeaning)}
            </span>
            {individual?.wikipediaUrl && (
              <a
                href={individual.wikipediaUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={(e) => e.stopPropagation()}
                className="text-gray-400 hover:text-blue-500 transition-colors"
                title="View on Wikipedia"
              >
                <Icon name="ExternalLink" size={11} />
              </a>
            )}
          </div>
        </div>
      </div>

      {/* ── STATS SECTION (Clickable & Expandable) ── */}
      <div className="px-3 pb-2 flex-1">
        {/* Net Worth - Highlighted (not expandable) */}
        <div className="flex items-center justify-between bg-emerald-50 rounded-lg px-3 py-2 mb-2">
          <span className="text-xs font-medium text-gray-600 flex items-center gap-1">
            <Icon name="DollarSign" size={12} className="text-emerald-600" />
            Net Worth
          </span>
          <span className="text-sm font-bold text-emerald-700">
            {formatNetWorth(individual?.netWorth)}
          </span>
        </div>

        {/* Expandable Stats */}
        <div className="space-y-0.5">
          <StatRow
            icon="Building2"
            iconColor="text-blue-500"
            label="Companies Owned/Founded"
            count={individual?.companiesOwnedCount || ownedCompanies.length || 0}
            items={ownedCompanies}
            previewItems={ownedLogoPreviews}
            renderItem={renderCompanyItem}
            navigate={navigate}
          />
          <StatRow
            icon="TrendingUp"
            iconColor="text-indigo-500"
            label="Companies Invested In"
            count={individual?.companiesInvestedCount || investedCompanies.length || 0}
            items={investedCompanies}
            previewItems={investedLogoPreviews}
            renderItem={renderCompanyItem}
            navigate={navigate}
          />
          <StatRow
            icon="Heart"
            iconColor="text-pink-500"
            label="Charities Supported"
            count={individual?.charitiesSupportedCount || charities.length || 0}
            items={charities}
            previewItems={charityPreviews}
            renderItem={renderCharityItem}
            navigate={navigate}
          />
          <StatRow
            icon="Landmark"
            iconColor="text-amber-600"
            label="PAC Donations"
            count={individual?.pacAssociationsCount || pacs.length || 0}
            items={pacs}
            previewItems={pacPreviews}
            renderItem={renderPacItem}
            navigate={navigate}
          />
          <StatRow
            icon="BookOpen"
            iconColor="text-teal-600"
            label="Think Tanks Funded"
            count={individual?.thinkTanksFundedCount || thinkTanks.length || 0}
            items={thinkTanks}
            previewItems={thinkTankPreviews}
            renderItem={renderThinkTankItem}
            navigate={navigate}
          />
        </div>
      </div>

      {/* ── POLITICAL SPECTRUM BAR ── */}
      <div className="px-4 py-2.5 border-t border-gray-100 bg-gray-50/50">
        <div className="flex justify-between text-[10px] text-gray-400 mb-1 font-medium">
          <span className="text-blue-500">Liberal</span>
          <span className="text-purple-500">Center</span>
          <span className="text-red-500">Conservative</span>
        </div>
        <div className="relative h-2 bg-gradient-to-r from-blue-500 via-purple-400 to-red-500 rounded-full overflow-visible">
          <div
            className="absolute w-3.5 h-3.5 bg-white border-2 border-gray-700 rounded-full shadow-md transform -translate-x-1/2 -translate-y-[3px]"
            style={{ left: `${getSpectrumPosition(individual?.politicalScore || 0)}%` }}
          />
        </div>
        <div className="flex justify-between mt-2 text-[10px]">
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-blue-500"></span>
            <span className="text-gray-500">Liberal PACs:</span>
            <span className="font-bold text-blue-700">
              {individual?.politicalContributions?.liberalPacs || 0}
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-red-500"></span>
            <span className="text-gray-500">Conservative PACs:</span>
            <span className="font-bold text-red-700">
              {individual?.politicalContributions?.conservativePacs || 0}
            </span>
          </div>
        </div>
      </div>

      {/* ── SHARE ROW ── */}
      <div className="px-4 pb-3 pt-1 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">Share</span>
        <ShareButtons
          compact={true}
          title={`${individual?.name} — Who You Makin' Rich`}
          text={`Check out ${individual?.name}'s financial connections, investments & political affiliations on Who You Makin' Rich`}
          url={`${window.location.origin}/individual-profile?id=${individual?.id}`}
        />
      </div>

      {/* ── HOVER OVERLAY ── */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none" />
    </div>
  );
};

export default ProminentFigureCard;
